import { Fragment, ReactNode, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Text,
  useClipboard,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import {
  AlertTriangleIcon,
  ArrowRightIcon,
  Card,
  CloudTransferIcon,
} from 'components/uikit';
import { DynamicCopyIcon } from 'components/uikit/CopyWrapper';
import { RefreshRequiredIcon } from 'components/uikit/icons';
import { chatwootIcon as ChatwootIcon } from 'components/uikit/icons/chatwoot';
import { DepositBank } from 'data/payments/models';
import {
  BankSettings,
  deeplinkRows,
  deeplinkUrls,
  DepositPaymentPage,
  depositValues,
  fillPlaceholders,
  LocalizedText,
  localized,
  matches,
  PageBlock,
  PageContext,
  PageData,
  PageDeeplink,
  pageImageUrl,
  PageStep,
} from 'data/payments/paymentPage';
import { AnalyticsEvents, InputType, trackEvent } from 'utils/analytics';
import { BrowserInfo } from 'utils/browserInfo';
import { useIsMobile } from 'utils/hooks/screenBreakpoints';
import { safeLs } from 'utils/localStorage';
import {
  openDeeplink,
  openDeeplinksWithReload,
  processDeeplinksWithReload,
} from 'utils/openDeeplink';

import { buttonStyles } from './BankDeeplinkButtons';
import { BankDeeplinkQR, downloadQrAsPng } from './BankDeeplinkQR';
import { PaymentClientControlsDefault } from './PaymentClientControlsDefault';
import { PaymentCredentialsBaseProps } from './PaymentCredentials';
import { PaymentFieldRow } from './PaymentFieldRow';
import { PaymentInstructionsModal } from './PaymentInstructionsModal';
import { PaymentPageQr } from './PaymentPageQr';
import { PaymentStatusControls } from './PaymentStatusControls';
import { PaymentUploadModal } from './PaymentUploadModal';

interface PaymentCredentialsFromPageProps extends PaymentCredentialsBaseProps {
  data: DepositPaymentPage & { page: PageData };
  crossborderAmount: string | null;
  crossborderCurrency: string | null;
  showChatwoot?: boolean;
}

/** `<bold>…</bold>` of the texts. */
const rich = (text: string): ReactNode =>
  text.split(/(<bold>.*?<\/bold>)/g).map((part, index) => {
    const bold = /^<bold>(.*)<\/bold>$/.exec(part);
    return bold ? (
      <Text as='span' key={index} fontWeight='700'>
        {bold[1]}
      </Text>
    ) : (
      <Fragment key={index}>{part}</Fragment>
    );
  });

const COPY_EVENTS: Partial<Record<string, InputType>> = {
  amount: 'amount',
  baseAmount: 'amount',
  name: 'recipient',
  bank: 'bank',
  operator: 'bank',
};

/** Opens the links of a deeplink row in order, like the bank buttons of the old page. */
const openLinks = async (links: string[], id: string) => {
  if (links.length > 1) {
    if (openDeeplinksWithReload(links, id))
      await processDeeplinksWithReload(id);
  } else if (links[0]) {
    await openDeeplink(links[0]);
  }
};

/**
 * The payment page drawn from the data of the method (plan section 9): the
 * blocks in order, each when its condition matches and its data is there.
 * Around them the same controls as the old page: timer, paid / cancel,
 * receipt upload, chat hint.
 */
export function PaymentCredentialsFromPage({
  id,
  acquirer,
  method,
  amount,
  currency,
  credentials: rawCredentials,
  failureRedirectLink,
  fromBank,
  status,
  timeLeft,
  paid,
  onPaid,
  onCancel,
  onInputCopied,
  onUploadClick,
  onUploadComplete,
  isUploadLoading,
  uploadModalIsOpen,
  uploadModalOnClose,
  crossborderAmount,
  crossborderCurrency,
  showChatwoot,
  data,
}: PaymentCredentialsFromPageProps) {
  const { t, i18n } = useTranslation('payments');
  const isMobile = useIsMobile();
  const instructions = useDisclosure();
  const [instructionsKit, setInstructionsKit] = useState<string | null>(null);
  const { page, kits, countryCode } = data;

  // A QR is not ready until postprocessing is done
  const credentials = useMemo(
    () =>
      rawCredentials.postProcessStatus && rawCredentials.type === 'QR'
        ? { ...rawCredentials, value: null }
        : rawCredentials,
    [rawCredentials],
  );
  const bank = fromBank ?? null;
  const bankSettings: BankSettings = (bank && page.bankSettings[bank]) || {};
  const values = {
    ...depositValues({
      amount,
      currency,
      crossborderAmount,
      crossborderCurrency,
      credentials,
      fromBank: bank,
      countryCode,
    }),
    ...(bankSettings.buttonName
      ? { buttonName: localized(bankSettings.buttonName, i18n.language) }
      : {}),
    ...(bankSettings.name
      ? { payerBank: localized(bankSettings.name, i18n.language) }
      : {}),
  };
  const context: PageContext = {
    bank,
    os: isMobile ? BrowserInfo.mobileOS ?? 'android' : 'desktop',
    isMobile,
    transferType: credentials.type,
    values,
  };

  // The Android deeplink of the payer bank for the credentials type, as the
  // old page took getDeeplinks(..., 'android')[0]
  const firstDeeplinkOf = () =>
    page.deeplinks.find(
      ({ os, app, transferType }) =>
        os === 'android' &&
        app === bank &&
        transferType === context.transferType,
    );
  const text = (value: LocalizedText | undefined) =>
    rich(fillPlaceholders(localized(value, i18n.language), values));
  const deeplinksOf = (style: 'buttons' | 'upiApps' | 'elqrApps') =>
    deeplinkRows(page, style, context);
  const urlsOf = (row: PageDeeplink) =>
    deeplinkUrls(row, page, { amount, credentials, countryCode });

  const [usedLinks, setUsedLinks] = useState<string[]>(() => {
    try {
      return JSON.parse(safeLs?.getItem(`@page_links_used/${id}`) ?? '[]');
    } catch {
      return [];
    }
  });

  const saveQr = () => {
    if (!credentials.value || !method) return;
    trackEvent(AnalyticsEvents.PAYMENT_QR_SAVED, {
      order_id: id,
      payment_method: method,
      amount_base: amount,
      bank: bank ?? undefined,
    });
    const isAndroid = context.os === 'android';
    downloadQrAsPng(credentials.value, `qr-${id}.png`, method, {
      ...(isAndroid
        ? {
            canvasWidth: 500,
            canvasHeight: 1000,
            qrWidthRatio: 0.7,
            qrHeightRatio: 0.7,
            offsetXRatio: 0.17,
            offsetYRatio: 0.42,
          }
        : {}),
      backgroundColor: bankSettings.darkQr ? '#1C1C1E' : undefined,
      center: bankSettings.darkQr,
    });
  };

  const bankPay = () => {
    if (!bankSettings.payLink) return;
    trackEvent(AnalyticsEvents.PAYMENT_BANK_PAY_LINK_CLICKED, {
      order_id: id,
      payment_method: method ?? '',
      amount_base: amount,
      bank: bank ?? undefined,
    });
    window.open(bankSettings.payLink, '_blank');
  };

  const isShown = (block: PageBlock) => {
    if (!matches(block.when, context)) return false;
    switch (block.type) {
      case 'bankSelect':
        return false;
      case 'bankLogo':
        return Boolean(bankSettings.payImage);
      case 'field':
        return Boolean(values[block.field]);
      case 'qr':
        return block.source === 'requisite'
          ? credentials.type !== 'QR' ||
              Boolean(credentials.value) ||
              Boolean(credentials.postProcessStatus)
          : block.source === 'firstDeeplink'
          ? Boolean(firstDeeplinkOf())
          : block.source !== 'upiLink' || Boolean(credentials.value);
      case 'deeplinks':
        return deeplinksOf(block.style).length > 0;
      case 'link':
        return !(block.once && usedLinks.includes(block.url));
      case 'banner':
        return (
          block.on === 'always' || credentials.postProcessStatus === 'FAILED'
        );
      case 'instructions':
        return Boolean(kits[block.kit]?.length);
      case 'steps':
        return block.steps.some((step) => matches(step.when, context));
      default:
        return true;
    }
  };

  const renderQr = (block: Extract<PageBlock, { type: 'qr' }>) => {
    const firstDeeplink = firstDeeplinkOf();
    const qr =
      block.source === 'requisite' ? (
        <BankDeeplinkQR
          id={id}
          fromBank={bank}
          method={method}
          value={credentials.value}
          postProcessStatus={credentials.postProcessStatus}
        />
      ) : block.source === 'deeplinkPage' ? (
        <BankDeeplinkQR id={id} fromBank={bank} method={method} />
      ) : (
        <PaymentPageQr
          value={
            block.source === 'upiLink'
              ? `upi://pay?${new URLSearchParams({
                  pa: credentials.value ?? '',
                  am: amount,
                  cu: currency,
                }).toString()}`
              : block.source === 'firstDeeplink' && firstDeeplink
              ? urlsOf(firstDeeplink)[0]
              : undefined
          }
        />
      );
    const onTemplate =
      block.onTemplate &&
      !isMobile &&
      bankSettings.qrTemplate &&
      !bankSettings.simple;

    return (
      <VStack spacing='8px' w='100%'>
        {onTemplate && bankSettings.qrTemplate ? (
          <Box position='relative' w='245px'>
            <Box
              as='img'
              src={pageImageUrl(bankSettings.qrTemplate)}
              alt=''
              w='100%'
              userSelect='none'
            />
            <Box
              position='absolute'
              top='50%'
              left='50%'
              transform='translate(-50%, -50%)'
            >
              {qr}
            </Box>
          </Box>
        ) : (
          <Flex justify='center'>{qr}</Flex>
        )}
        {block.caption ? (
          <Text fontSize='sm' color='grey.secondaryText' textAlign='center'>
            {text(block.caption)}
          </Text>
        ) : null}
        {block.download && credentials.value ? (
          <Button variant='paymentOutline' size='sm' onClick={saveQr}>
            {t('payment_guide_vnd_save_qr')}
          </Button>
        ) : null}
      </VStack>
    );
  };

  const renderDeeplinks = (style: 'buttons' | 'upiApps' | 'elqrApps') => (
    <VStack w='100%' spacing='8px'>
      {deeplinksOf(style).map((row) => {
        const links = urlsOf(row);
        if (row.display === 'qr')
          return (
            <VStack key={`${row.os}${row.app}`}>
              <PaymentPageQr value={links[0]} />
              <Text fontSize='sm'>{row.app}</Text>
            </VStack>
          );
        const styles = buttonStyles[row.app as DepositBank];
        const open = () => {
          trackEvent(AnalyticsEvents.PAYMENT_LINK_CLICKED, {
            order_id: id,
            payment_method: method ?? '',
            amount_base: amount,
          });
          void openLinks(links, id);
        };
        return styles ? (
          <Button
            key={row.app}
            w='100%'
            p='0'
            backgroundImage={styles.backgroundImage}
            backgroundColor={styles.backgroundColor}
            _hover={{ backgroundColor: styles.backgroundColor }}
            onClick={open}
          >
            <HStack h='100%' alignItems='center' spacing='6px'>
              <Text
                color={styles.fontColor}
                fontSize='lg'
                fontWeight='semibold'
              >
                {t('pay_in_app')}
              </Text>
              {styles.svg}
            </HStack>
          </Button>
        ) : (
          <Button
            key={row.app}
            w='100%'
            variant='paymentOutline'
            onClick={open}
          >
            {t('pay_in_app')} {row.app}
          </Button>
        );
      })}
    </VStack>
  );

  /** A step as on the old page: the number in line with the text, its button or image beside. */
  /** A step as on the old page: the number in line with the text, its button or image beside. */
  const renderStep = (step: PageStep, number: number) => {
    const image =
      step.image ?? (step.bankImage ? bankSettings[step.bankImage] : undefined);
    const title = step.titleByOs?.[context.os] ?? step.title;

    return (
      <HStack key={number} align='center' spacing='12px' w='100%'>
        <VStack align='stretch' spacing='6px' flex='1' minW={0}>
          <Text
            fontWeight='400'
            fontSize={['md', 'xl']}
            color='grey.primaryText'
            lineHeight='1.4'
          >
            {number}. {text(title)}
          </Text>
          {step.description ? (
            <Text fontSize='sm' color='grey.secondaryText'>
              {text(step.description)}
            </Text>
          ) : null}
          {step.copy.map((field) => (
            <PaymentFieldRow
              key={field}
              label=''
              value={values[field] ?? ''}
              onCopyFunc={() =>
                onInputCopied(COPY_EVENTS[field] ?? 'credentials')
              }
            />
          ))}
        </VStack>
        {step.action === 'saveQr' && credentials.value ? (
          <Button
            variant='paymentOutline'
            size='sm'
            flexShrink={0}
            onClick={saveQr}
          >
            {t('payment_guide_vnd_save_qr')}
          </Button>
        ) : null}
        {step.action === 'bankPay' && bankSettings.payLink ? (
          <Button
            variant='unstyled'
            h='auto'
            flexShrink={0}
            onClick={bankPay}
            _focus={{ boxShadow: 'none' }}
          >
            {bankSettings.payImage ? (
              <Box
                as='img'
                src={pageImageUrl(bankSettings.payImage)}
                alt=''
                w='70px'
              />
            ) : (
              t('pay_in_app')
            )}
          </Button>
        ) : null}
        {image ? (
          <Box
            as='img'
            src={pageImageUrl(image)}
            alt=''
            w={['90px', '120px']}
            flexShrink={0}
            borderRadius='8px'
          />
        ) : null}
      </HStack>
    );
  };

  const renderBlock = (block: PageBlock): ReactNode => {
    switch (block.type) {
      case 'title':
        // The right column opens with the details title, as on the old page
        if (block.column === 'right' && page.columns)
          return (
            <Text fontWeight='semibold' fontSize='xl'>
              {text(block.text)}
            </Text>
          );
        return (
          <Text
            as='h3'
            fontSize={['18px', '20px']}
            fontWeight='medium'
            variant='numeric'
            color='payment.titleText'
            lineHeight='1'
            textAlign='center'
          >
            {text(block.text)}
          </Text>
        );
      case 'amount':
        return (
          <Text
            as='h1'
            fontSize={['56px', '64px']}
            fontWeight='medium'
            variant='numeric'
            color='grey.primaryText'
            lineHeight='1'
            textAlign='center'
          >
            {amount}
            <sub style={{ bottom: 0 }}>{currency}</sub>
          </Text>
        );
      case 'bankLogo':
        // In the corner of the card on desktop, in line on mobile
        return bankSettings.payImage ? (
          <Box
            as='img'
            src={pageImageUrl(bankSettings.payImage)}
            alt=''
            w='80px'
            h='auto'
            {...(isMobile
              ? {}
              : {
                  position: 'absolute',
                  top: '12px',
                  left: '12px',
                  zIndex: 10,
                })}
          />
        ) : null;
      case 'field': {
        const value = values[block.field] ?? '';
        const copyValue =
          isMobile &&
          block.mobileCopyStrip &&
          value.startsWith(block.mobileCopyStrip)
            ? value.slice(block.mobileCopyStrip.length)
            : value;
        return (
          <PaymentFieldRow
            label={block.label ? localized(block.label, i18n.language) : ''}
            value={copyValue}
            displayedValue={
              block.truncate && value.length > 24
                ? `${value.slice(0, 12)}…${value.slice(-8)}`
                : value
            }
            customClass={
              block.field === 'amount' || block.field === 'baseAmount'
                ? ''
                : 'ph-no-capture'
            }
            onCopyFunc={() =>
              onInputCopied(COPY_EVENTS[block.field] ?? 'credentials')
            }
          />
        );
      }
      case 'qr':
        return renderQr(block);
      case 'deeplinks':
        return renderDeeplinks(block.style);
      case 'link':
        return (
          <Button
            w='100%'
            variant='paymentOutline'
            onClick={() => openLink(block)}
          >
            {text(block.label)}
          </Button>
        );
      case 'banner': {
        const image = block.bankErrorImage
          ? bankSettings.errorImage ?? block.image
          : block.image;
        const hasDeeplinks = deeplinksOf('buttons').length > 0;
        const body = !isMobile
          ? block.text
          : hasDeeplinks && block.textMobileDeeplink
          ? block.textMobileDeeplink
          : block.textMobile ?? block.text;
        // As the old QR error banners: the texts, an arrow, the picture
        return (
          <HStack spacing='16px' w='100%' align='center'>
            <VStack align='start' spacing='6px' flex='1'>
              {block.title ? (
                <Text fontSize='sm' color='grey.primaryText'>
                  {text(block.title)}
                </Text>
              ) : null}
              <Text fontSize='sm' color='grey.primaryText' lineHeight='1.4'>
                {text(body)}
              </Text>
            </VStack>
            {image ? (
              <>
                <ArrowRightIcon
                  color='grey.primaryText'
                  boxSize='20px'
                  flexShrink={0}
                />
                <Box
                  as='img'
                  src={pageImageUrl(image)}
                  alt=''
                  w='120px'
                  flexShrink={0}
                  borderRadius='8px'
                />
              </>
            ) : null}
          </HStack>
        );
      }
      case 'notice':
        // The transfer hint is an illustration with a caption, as on the old page
        if (block.icon === 'transfer')
          return (
            <VStack spacing='12px' textAlign='center'>
              <Flex
                w={['180px', '200px']}
                h={['180px', '200px']}
                bg={['transparent', 'payment.qrBackground']}
                borderRadius='13px'
                justify='center'
                align='center'
              >
                <CloudTransferIcon
                  cloudColor='payment.cloudColor'
                  arrowColor='payment.arrowColor'
                />
              </Flex>
              <Text
                fontSize='md'
                fontWeight='medium'
                color='grey.tertiaryText'
                maxW='360px'
              >
                {text(block.text)}
              </Text>
            </VStack>
          );
        if (!block.icon && !block.title)
          return (
            <Text
              fontWeight='200'
              fontSize='md'
              color='payment.titleText'
              lineHeight='1.2'
              textAlign='center'
            >
              {text(block.text)}
            </Text>
          );
        if (block.icon === 'warning')
          return (
            <HStack
              w='100%'
              bg='warning.background'
              borderWidth='1px'
              borderColor='warning.button'
              borderRadius='10px'
              p={['10px 12px', '12px 16px']}
              spacing='10px'
              align={block.title ? 'flex-start' : 'center'}
            >
              <AlertTriangleIcon
                width='22px'
                height='22px'
                stroke='warning.text'
                flexShrink={0}
              />
              <VStack align='start' spacing='6px'>
                {block.title ? (
                  <Text
                    fontWeight='700'
                    fontSize={['md', 'lg']}
                    color='warning.text'
                    lineHeight='1.4'
                  >
                    {text(block.title)}
                  </Text>
                ) : null}
                <Text
                  fontWeight={block.title ? '400' : '700'}
                  fontSize={block.title ? 'sm' : ['md', 'lg']}
                  color='warning.text'
                  lineHeight='1.4'
                >
                  {text(block.text)}
                </Text>
              </VStack>
            </HStack>
          );
        return (
          <HStack
            align='flex-start'
            spacing='10px'
            w='100%'
            p='12px'
            borderRadius='12px'
            bgColor='payment.notice'
          >
            {block.icon === 'refresh' ? (
              <RefreshRequiredIcon
                boxSize='18px'
                color='payment.noticeText'
                flexShrink={0}
              />
            ) : block.icon === 'warning' ? (
              <AlertTriangleIcon
                boxSize='18px'
                stroke='warning.text'
                flexShrink={0}
              />
            ) : block.icon === 'transfer' ? (
              <CloudTransferIcon
                boxSize='28px'
                cloudColor='payment.cloudColor'
                arrowColor='payment.arrowColor'
                flexShrink={0}
              />
            ) : null}
            <VStack align='start' spacing='4px'>
              {block.title ? (
                <Text fontWeight='semibold'>{text(block.title)}</Text>
              ) : null}
              <Text fontSize='sm'>{text(block.text)}</Text>
            </VStack>
          </HStack>
        );
      case 'instructions':
        return (
          <Button
            w='100%'
            variant='paymentOutline'
            borderRadius='8px'
            fontWeight='semibold'
            fontSize={['sm', 'md']}
            onClick={() => {
              setInstructionsKit(block.kit);
              instructions.onOpen();
            }}
          >
            {t('payment_instructions')}
          </Button>
        );
      case 'steps': {
        const steps = block.steps.filter((step) => matches(step.when, context));
        return (
          <VStack align='stretch' spacing='12px' w='100%'>
            {block.title ? (
              <Text fontWeight='semibold'>{text(block.title)}</Text>
            ) : null}
            {steps.map((step, index) => renderStep(step, index + 1))}
          </VStack>
        );
      }
      default:
        return null;
    }
  };

  const shown = page.layout.filter(isShown);
  const hasColumns = page.columns && !isMobile;

  const timer = (
    <Box bg='payment.timeBar' borderRadius='13px' p='10px 20px' flexShrink={0}>
      {!(paid || status === 'PAID') && (
        <Text
          fontSize={['lg', 'xl']}
          color='payment.timeBarText'
          fontWeight='medium'
        >
          {timeLeft}
        </Text>
      )}
    </Box>
  );

  const openLink = (block: Extract<PageBlock, { type: 'link' }>) => {
    if (block.once) {
      const next = [...usedLinks, block.url];
      setUsedLinks(next);
      try {
        safeLs?.setItem(`@page_links_used/${id}`, JSON.stringify(next));
      } catch {
        // Hidden for this page view only
      }
    }
    void openDeeplink(fillPlaceholders(block.url, values));
  };

  /**
   * The bank logo and the link that stand right before a titled list on
   * mobile: drawn in its panel like the old VND_TG_AN instructions, the logo
   * beside the title, or the link as a row with an arrow to the logo.
   */
  type StepsHeader = {
    logo: boolean;
    link?: Extract<PageBlock, { type: 'link' }>;
  };

  const stepsOf = (
    block: Extract<PageBlock, { type: 'steps' }>,
    header?: StepsHeader,
  ) => {
    const steps = block.steps.filter((step) => matches(step.when, context));
    const logo =
      header?.logo && bankSettings.payImage ? (
        <Box
          as='img'
          src={pageImageUrl(bankSettings.payImage)}
          alt=''
          w='70px'
          h='auto'
          flexShrink={0}
        />
      ) : null;
    // A titled list is the text instructions of the old page (VND_TG_AN, IDR_TG_WALLET)
    if (block.title)
      return (
        <VStack align='stretch' spacing={['10px', '12px']} w='100%'>
          <HStack justify='space-between' align='center'>
            <Text
              fontWeight='600'
              fontSize={['md', 'lg']}
              color='grey.primaryText'
            >
              {text(block.title)}
            </Text>
            {header?.link ? null : logo}
          </HStack>
          {header?.link && logo ? (
            <HStack justify='space-between' align='center' spacing='10px'>
              <Text
                fontWeight='500'
                fontSize='md'
                color='grey.primaryText'
                lineHeight='1.4'
                flex='1'
              >
                {text(header.link.label)}
              </Text>
              <ArrowRightIcon
                color='grey.primaryText'
                boxSize='20px'
                flexShrink={0}
              />
              <Button
                onClick={() => header.link && openLink(header.link)}
                variant='unstyled'
                p='0'
                h='auto'
                w='auto'
                minW='auto'
                display='flex'
                alignItems='center'
                flexShrink={0}
                _hover={{ opacity: 0.8 }}
                _active={{ opacity: 0.6 }}
                _focus={{ boxShadow: 'none' }}
              >
                {logo}
              </Button>
            </HStack>
          ) : null}
          {steps.map((step, index) => (
            <HStack key={index} align='flex-start' spacing='12px'>
              <Flex
                flexShrink={0}
                w='24px'
                h='24px'
                borderRadius='full'
                bg='payment.timeBar'
                color='payment.timeBarText'
                fontSize='sm'
                fontWeight='600'
                align='center'
                justify='center'
              >
                {index + 1}
              </Flex>
              <Box minW='0'>
                <Text
                  fontWeight='400'
                  fontSize={['md', 'lg']}
                  color='grey.primaryText'
                  lineHeight='24px'
                >
                  {text(step.titleByOs?.[context.os] ?? step.title)}
                </Text>
                {step.copy.length ? (
                  <Flex wrap='wrap' columnGap='8px'>
                    {step.copy.map((field) =>
                      values[field] ? (
                        <CopyChip
                          key={field}
                          value={values[field] as string}
                          onCopied={() =>
                            onInputCopied(COPY_EVENTS[field] ?? 'credentials')
                          }
                        />
                      ) : null,
                    )}
                  </Flex>
                ) : null}
              </Box>
            </HStack>
          ))}
        </VStack>
      );
    return (
      <VStack align='stretch' spacing={['8px', '4px']} w='100%'>
        {steps.map((step, index) => renderStep(step, index + 1))}
      </VStack>
    );
  };

  /** Steps next to a QR share a panel: QR on the right on desktop, above on mobile. */
  const panel = (
    steps: Extract<PageBlock, { type: 'steps' }>,
    qr: Extract<PageBlock, { type: 'qr' }>,
    extras: PageBlock[],
  ) => (
    <Box
      w='100%'
      p={['0', '20px']}
      bg={['transparent', 'payment.qrBackground']}
      borderRadius='13px'
    >
      {isMobile ? (
        <VStack spacing='16px' w='100%'>
          {renderQr(qr)}
          <Box
            w='100%'
            p='16px'
            borderRadius='12px'
            bg='rgba(255, 255, 255, 0.5)'
          >
            {stepsOf(steps)}
          </Box>
        </VStack>
      ) : (
        <Flex justify='space-between' align='center' gap='15px'>
          <Box w='70%'>{stepsOf(steps)}</Box>
          <Box flexShrink={0}>{renderQr(qr)}</Box>
        </Flex>
      )}
      {/* Warnings and banners after the panel belong to it, as on the old page */}
      {extras.map((extra, index) => (
        <Box key={index} mt={['16px', '20px']}>
          {extra.type === 'banner' ? <Divider mb={['16px', '20px']} /> : null}
          {renderBlock(extra)}
        </Box>
      ))}
    </Box>
  );

  /**
   * The blocks of a column in order, as the old page lays them out: steps
   * and a QR next to each other in one panel, with the warnings and banners
   * that follow; fields as one list; the timer in line with `timerWith`.
   */
  // A one-column page on mobile (the QR pages of the old page) shows the chat
  // hint as a pill above the instructions button, not in the footer
  const isChatHintAboveInstructions =
    showChatwoot &&
    isMobile &&
    !page.columns &&
    shown.some((block) => block.type === 'qr') &&
    shown.some((block) => block.type === 'instructions');
  const chatHint = (borderRadius: string) => (
    <HStack
      w='100%'
      bg='payment.qrBackground'
      borderWidth='1px'
      borderColor='payment.qrBorder'
      borderRadius={borderRadius}
      p='10px 16px'
      justify='center'
      spacing='8px'
    >
      <Text fontSize='sm' fontWeight='semibold' color='payment.titleText'>
        {t('payment_chat_hint')}
      </Text>
      <ChatwootIcon boxSize='18px' flexShrink={0} />
    </HStack>
  );

  const layout = (
    blocks: PageBlock[],
    timerWith: 'instructions' | 'title' | null,
  ) => {
    const nodes: ReactNode[] = [];
    let hasTimer = false;
    const isExtra = (block: PageBlock | undefined) =>
      block?.type === 'banner' ||
      (block?.type === 'notice' && block.icon === 'warning');
    for (let index = 0; index < blocks.length; index += 1) {
      let block = blocks[index] as PageBlock;
      const next = blocks[index + 1];
      const pair =
        block.type === 'steps' && next?.type === 'qr'
          ? ([block, next] as const)
          : block.type === 'qr' && next?.type === 'steps'
          ? ([next, block] as const)
          : null;
      if (pair) {
        index += 1;
        const extras: PageBlock[] = [];
        while (isExtra(blocks[index + 1])) {
          extras.push(blocks[index + 1] as PageBlock);
          index += 1;
        }
        nodes.push(<Box key={index}>{panel(pair[0], pair[1], extras)}</Box>);
      } else if (block.type === 'field') {
        // Fields in a row make one list, divided like the old page
        const fields: PageBlock[] = [block];
        while (blocks[index + 1]?.type === 'field') {
          fields.push(blocks[index + 1] as PageBlock);
          index += 1;
        }
        nodes.push(
          <VStack key={index} spacing={0} align='stretch' w='100%'>
            {fields.map((field, position) => (
              <Fragment key={position}>
                {position ? <Divider /> : null}
                {renderBlock(field)}
              </Fragment>
            ))}
          </VStack>,
        );
      } else if (
        block.type === 'steps' ||
        (isMobile &&
          (block.type === 'bankLogo' || block.type === 'link') &&
          (() => {
            let ahead = index;
            while (
              blocks[ahead]?.type === 'bankLogo' ||
              blocks[ahead]?.type === 'link'
            )
              ahead += 1;
            const target = blocks[ahead];
            return target?.type === 'steps' && Boolean(target.title);
          })())
      ) {
        const header: StepsHeader = { logo: false };
        while (block.type === 'bankLogo' || block.type === 'link') {
          if (block.type === 'bankLogo') header.logo = true;
          else header.link = block;
          index += 1;
          block = blocks[index] as PageBlock;
        }
        if (block.type !== 'steps') continue;
        // Without the logo the link stays a button of its own
        if (header.link && !(header.logo && bankSettings.payImage)) {
          nodes.push(
            <Box key={`${index}-link`} w='100%'>
              {renderBlock(header.link)}
            </Box>,
          );
          header.link = undefined;
        }
        nodes.push(
          <Box
            key={index}
            p={['16px', '20px']}
            bg={
              block.title
                ? 'payment.qrBackground'
                : ['transparent', 'payment.qrBackground']
            }
            borderRadius='13px'
          >
            {stepsOf(block, header)}
          </Box>,
        );
      } else if (timerWith && !hasTimer && block.type === timerWith) {
        hasTimer = true;
        if (isChatHintAboveInstructions && block.type === 'instructions')
          nodes.push(<Box key='chat-hint'>{chatHint('999px')}</Box>);
        nodes.push(
          <HStack
            key={index}
            justify='space-between'
            align='center'
            spacing='12px'
            w='100%'
          >
            <Box flex='1'>{renderBlock(block)}</Box>
            {timer}
          </HStack>,
        );
      } else {
        nodes.push(
          <Box
            key={index}
            w='100%'
            // On a two-column page the instructions button sits at the bottom
            mt={
              !timerWith && block.type === 'instructions'
                ? 'auto !important'
                : undefined
            }
          >
            {renderBlock(block)}
          </Box>,
        );
      }
    }
    return { nodes, hasTimer };
  };

  // A two-column page on mobile keeps the old order: the details title with
  // the timer, the fields, then the instructions button
  const mobileColumns = (() => {
    const leftBlocks = shown.filter((block) => block.column !== 'right');
    const rightBlocks = shown.filter((block) => block.column === 'right');
    const instructionsBlocks = leftBlocks.filter(
      (block) => block.type === 'instructions',
    );
    // The details end with the last field, or with the text instructions
    // that stand in place of the fields (IDR_TG_WALLET)
    const lastField = rightBlocks.reduce(
      (last, block, position) =>
        block.type === 'field' || block.type === 'steps' ? position : last,
      -1,
    );
    return [
      ...leftBlocks.filter((block) => block.type !== 'instructions'),
      ...rightBlocks.slice(0, lastField + 1),
      ...instructionsBlocks,
      ...rightBlocks.slice(lastField + 1),
    ];
  })();
  const single = page.columns
    ? layout(mobileColumns, 'title')
    : layout(shown, 'instructions');
  const left = layout(
    shown.filter((block) => block.column !== 'right'),
    null,
  );
  const right = layout(
    shown.filter((block) => block.column === 'right'),
    'title',
  );
  const timerRow = <Flex justify='flex-end'>{timer}</Flex>;

  const footer = (
    <VStack spacing='16px' align='stretch' w='100%'>
      {showChatwoot && !isChatHintAboveInstructions ? chatHint('10px') : null}
      {acquirer === 'TEST' ? (
        <PaymentStatusControls id={id} />
      ) : (
        <PaymentClientControlsDefault
          id={id}
          failureRedirectLink={failureRedirectLink}
          onPaid={onPaid}
          onCancel={onCancel}
        />
      )}
    </VStack>
  );

  return (
    <VStack spacing={['0', '24px']} align='center' w='100%' zIndex={1}>
      <Card
        bg={['transparent', 'grey.white']}
        boxShadow='none'
        p='20px'
        w={['100vw', hasColumns ? 'auto' : '550px']}
        maxW={['100%', hasColumns ? '900px' : '100%']}
        mx='auto'
        borderWidth={['0', '1px']}
        borderColor={['transparent', 'grey.border']}
        borderRadius={['0', '8px']}
        backdropFilter='blur(14px)'
        position='relative'
      >
        <VStack spacing='16px' align='stretch' w='100%'>
          {hasColumns ? (
            // As the old page: the left column as wide as its content, both
            // as high as the card; the buttons at the bottom of each
            <HStack align='stretch' spacing='20px' w='100%'>
              <VStack
                spacing='16px'
                flexShrink={0}
                align='stretch'
                maxW='380px'
              >
                {left.nodes}
              </VStack>
              <VStack
                spacing='15px'
                flex='1'
                align='stretch'
                px='10px'
                minW={0}
              >
                {right.hasTimer ? null : timerRow}
                {right.nodes}
                <Box mt='auto !important'>{footer}</Box>
              </VStack>
            </HStack>
          ) : (
            <VStack spacing='16px' align='stretch' w='100%'>
              {single.nodes}
              {single.hasTimer ? null : timerRow}
            </VStack>
          )}

          {hasColumns ? null : footer}
        </VStack>
      </Card>

      <PaymentUploadModal
        isOpen={uploadModalIsOpen}
        onClose={uploadModalOnClose}
        onUploadClick={onUploadClick}
        isLoading={isUploadLoading}
        onUpload={onUploadComplete}
      />
      {instructionsKit ? (
        <PaymentInstructionsModal
          isOpen={instructions.isOpen}
          onClose={instructions.onClose}
          depositId={id}
          method={method}
          credentials={credentials}
          fromBank={fromBank}
          amount={values.amount}
          pageGuides={kits[instructionsKit] ?? []}
          pageValues={values}
        />
      ) : null}
    </VStack>
  );
}

/** A value to copy, as a chip: the text instructions of the old page. */
function CopyChip({
  value,
  onCopied,
}: {
  value: string;
  onCopied: () => void;
}) {
  const { onCopy, hasCopied } = useClipboard(value);

  return (
    <HStack
      as='button'
      type='button'
      mt='6px'
      spacing='6px'
      px='10px'
      py='3px'
      bg='grey.white'
      borderWidth='1px'
      borderColor={hasCopied ? 'success.button' : 'grey.border'}
      borderRadius='8px'
      textAlign='start'
      className='ph-no-capture'
      onClick={() => {
        onCopied();
        onCopy();
      }}
    >
      <Text
        as='span'
        fontSize={['md', 'lg']}
        fontWeight='600'
        color='grey.primaryText'
        wordBreak='break-all'
      >
        {value}
      </Text>
      <DynamicCopyIcon
        hasCopied={hasCopied}
        boxSize='16px'
        color={hasCopied ? 'success.button' : 'grey.primaryText'}
      />
    </HStack>
  );
}
